/**
 * Telefon numarası yardımcıları — backend'deki `App\Support\PhoneNumber` ile
 * AYNI kuralları uygular. İkisi birbirinden saparsa veli giriş yapamaz:
 * numara `users.phone` üzerinde benzersiz ve veli sisteme telefonuyla giriyor.
 *
 * KANONİK BİÇİM: ülke kodu + ulusal numara, artı/boşluk yok.
 *   '905372799324'
 */

/**
 * Ülke kodları — UZUN OLAN ÖNCE. Sıra kritik: '90' ile '994' aynı listede ve
 * kısa olan önce denenirse uzun kodlu numara yanlış bölünür.
 * Backend'deki `PhoneNumber::ULKE_KODLARI` ile aynı sırada tutulmalı.
 */
export const COUNTRIES = [
  { code: '90',  name: 'Türkiye',         flag: '🇹🇷' },
  { code: '49',  name: 'Almanya',         flag: '🇩🇪' },
  { code: '31',  name: 'Hollanda',        flag: '🇳🇱' },
  { code: '32',  name: 'Belçika',         flag: '🇧🇪' },
  { code: '43',  name: 'Avusturya',       flag: '🇦🇹' },
  { code: '41',  name: 'İsviçre',         flag: '🇨🇭' },
  { code: '44',  name: 'Birleşik Krallık', flag: '🇬🇧' },
  { code: '33',  name: 'Fransa',          flag: '🇫🇷' },
  { code: '39',  name: 'İtalya',          flag: '🇮🇹' },
  { code: '34',  name: 'İspanya',         flag: '🇪🇸' },
  { code: '46',  name: 'İsveç',           flag: '🇸🇪' },
  { code: '47',  name: 'Norveç',          flag: '🇳🇴' },
  { code: '45',  name: 'Danimarka',       flag: '🇩🇰' },
  { code: '48',  name: 'Polonya',         flag: '🇵🇱' },
  { code: '40',  name: 'Romanya',         flag: '🇷🇴' },
  { code: '30',  name: 'Yunanistan',      flag: '🇬🇷' },
  { code: '359', name: 'Bulgaristan',     flag: '🇧🇬' },
  { code: '389', name: 'K. Makedonya',    flag: '🇲🇰' },
  { code: '383', name: 'Kosova',          flag: '🇽🇰' },
  { code: '355', name: 'Arnavutluk',      flag: '🇦🇱' },
  { code: '353', name: 'İrlanda',         flag: '🇮🇪' },
  { code: '994', name: 'Azerbaycan',      flag: '🇦🇿' },
  { code: '995', name: 'Gürcistan',       flag: '🇬🇪' },
  { code: '971', name: 'BAE',             flag: '🇦🇪' },
  { code: '966', name: 'S. Arabistan',    flag: '🇸🇦' },
  { code: '964', name: 'Irak',            flag: '🇮🇶' },
  { code: '963', name: 'Suriye',          flag: '🇸🇾' },
  { code: '961', name: 'Lübnan',          flag: '🇱🇧' },
  { code: '20',  name: 'Mısır',           flag: '🇪🇬' },
  { code: '27',  name: 'Güney Afrika',    flag: '🇿🇦' },
  { code: '7',   name: 'Rusya',           flag: '🇷🇺' },
  { code: '1',   name: 'ABD / Kanada',    flag: '🇺🇸' },
];

export const DEFAULT_COUNTRY = '90';

// Ayırma her zaman UZUN koddan kısaya denenmeli (bkz. yukarıdaki not).
const CODES_BY_LENGTH = COUNTRIES.map(c => c.code).sort((a, b) => b.length - a.length);

/** Yalnızca rakamlar. */
export const digitsOnly = (raw) => String(raw ?? '').replace(/\D/g, '');

/**
 * Kanonik numarayı ekranda göstermek/forma doldurmak için ayırır.
 *   '905372799324' → { country: '90', national: '5372799324' }
 *
 * Ülke kodu çözülemezse tamamı ulusal kabul edilir — yanlış yerden bölüp
 * kullanıcıya bozuk numara göstermektense bölmemek iyidir.
 */
export const splitPhone = (canonical) => {
  const digits = digitsOnly(canonical);
  if (!digits) return { country: DEFAULT_COUNTRY, national: '' };

  for (const code of CODES_BY_LENGTH) {
    if (digits.startsWith(code) && digits.length > code.length) {
      return { country: code, national: digits.slice(code.length) };
    }
  }

  return { country: DEFAULT_COUNTRY, national: digits };
};

/**
 * Ülke kodu + kullanıcının yazdığı numaradan kanonik değeri üretir.
 *
 * Kullanıcı numaranın başına "+" ya da "00" koyduysa ülke kodunu KENDİSİ
 * söylemiştir; seçici ne olursa olsun onun yazdığı kazanır. Veli numarayı
 * WhatsApp'tan kopyalayıp yapıştırdığında tam olarak bu biçim geliyor.
 *
 * Backend `PhoneNumber::normalize` ile aynı davranış.
 */
export const buildPhone = (country, raw) => {
  const text = String(raw ?? '').trim();
  if (!text) return '';

  const explicitIntl = text.startsWith('+') || text.startsWith('00');
  let digits = digitsOnly(text);
  if (!digits) return '';

  if (explicitIntl) {
    return digits.replace(/^00/, '');
  }

  const code = digitsOnly(country) || DEFAULT_COUNTRY;

  // "905372799324" gibi ülke kodunu artısız yazan kullanıcı.
  if (digits.startsWith(code)) {
    const rest = digits.slice(code.length).replace(/^0+/, '');
    if (rest.length >= 9) return code + rest;
  }

  const national = digits.replace(/^0+/, '');
  return national ? code + national : '';
};

/** Ekranda okunur biçim: '905372799324' → '+90 5372799324' */
export const formatPhone = (canonical) => {
  const digits = digitsOnly(canonical);
  if (!digits) return '';

  const { country, national } = splitPhone(digits);
  return `+${country} ${national}`;
};
